import { useState, useEffect } from 'react'
import { FiCheckCircle, FiArrowRight, FiMinus, FiPlus, FiShield, FiStar, FiCreditCard, FiExternalLink } from 'react-icons/fi'
import { useAuth } from '../contexts/AuthContext'
import toast from 'react-hot-toast'
import { formatearFecha } from '../utils/fechas'

const API_URL = process.env.REACT_APP_API_URL || '/api'

const PLANES = {
  pro: {
    name: 'Pro',
    desc: 'Cumplimiento legal completo',
    priceMonthly: 1.50,
    priceAnnual: 1.25,
    features: [
      'Fichaje entrada/salida y pausas',
      'Correcciones directas por el admin',
      'Informes de jornada en PDF y Excel',
      'Gestión de centros y grupos',
      'Almacenamiento 4 años',
      'Soporte por email'
    ]
  },
  avanzado: {
    name: 'Avanzado',
    desc: 'Control total para tu empresa',
    priceMonthly: 2.95,
    priceAnnual: 2.46,
    features: [
      'Todo lo del plan Pro',
      'Gestión de ausencias y vacaciones',
      'Flujo de correcciones con empleados',
      'Informes avanzados y exportación',
      'Geolocalización de fichajes',
      'Notificaciones automáticas',
      'Soporte prioritario'
    ]
  }
}

export default function Suscripcion() {
  const { empleado, empresaInfo, refreshEmpleado } = useAuth()
  const [plan, setPlan] = useState(empresaInfo?.preferred_plan || 'pro')
  const [interval, setInterval_] = useState('yearly')
  const [slots, setSlots] = useState(5)
  const [loading, setLoading] = useState(false)
  const [billingStatus, setBillingStatus] = useState(null)
  const [loadingStatus, setLoadingStatus] = useState(true)

  const isAdmin = empleado?.rol === 'admin' || empleado?.rol === 'super_admin'
  const hasActiveSubscription = billingStatus?.subscriptionStatus === 'active'

  useEffect(() => {
    fetchBillingStatus()
  }, [])

  const fetchBillingStatus = async () => {
    try {
      const token = localStorage.getItem('token')
      const res = await fetch(`${API_URL}/billing/status`, {
        headers: { 'Authorization': `Bearer ${token}` }
      })
      if (res.ok) {
        const data = await res.json()
        setBillingStatus(data)
        if (data.slotsTotal > 0) setSlots(data.slotsTotal)
        if (data.plan && data.plan !== 'avanzado') setPlan(data.plan)
        if (data.billingInterval) setInterval_(data.billingInterval)
      }
    } catch (err) {
      console.error('Error fetching billing status:', err)
    } finally {
      setLoadingStatus(false)
    }
  }

  const handleCheckout = async () => {
    if (!isAdmin) return
    setLoading(true)
    try {
      const token = localStorage.getItem('token')
      const res = await fetch(`${API_URL}/billing/checkout`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ plan, interval, slots })
      })
      const data = await res.json()
      if (data.url) {
        window.location.href = data.url
      } else {
        toast.error(data.error || 'Error al iniciar el pago')
      }
    } catch (err) {
      toast.error('Error de conexión')
    } finally {
      setLoading(false)
    }
  }

  const handlePortal = async () => {
    setLoading(true)
    try {
      const token = localStorage.getItem('token')
      const res = await fetch(`${API_URL}/billing/portal`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      })
      const data = await res.json()
      if (data.url) {
        window.location.href = data.url
      } else {
        toast.error(data.error || 'Error al abrir el portal')
      }
    } catch (err) {
      toast.error('Error de conexión')
    } finally {
      setLoading(false)
    }
  }

  const handleAddSlots = async (additional) => {
    setLoading(true)
    try {
      const token = localStorage.getItem('token')
      const res = await fetch(`${API_URL}/billing/add-slots`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ additional_slots: additional })
      })
      const data = await res.json()
      if (res.ok) {
        toast.success(data.message)
        fetchBillingStatus()
        refreshEmpleado()
      } else {
        toast.error(data.error)
      }
    } catch (err) {
      toast.error('Error de conexión')
    } finally {
      setLoading(false)
    }
  }

  const selectedPlan = PLANES[plan]
  const pricePerSlot = interval === 'yearly' ? selectedPlan.priceAnnual : selectedPlan.priceMonthly
  const total = pricePerSlot * slots
  const intervalLabel = interval === 'yearly' ? 'año' : 'mes'
  const totalLabel = interval === 'yearly' ? (total * 12).toFixed(2) : total.toFixed(2)

  if (loadingStatus) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="text-[var(--ink-3)]">Cargando...</div>
      </div>
    )
  }

  // ── Suscripción activa: gestión de slots y portal ──
  if (hasActiveSubscription) {
    return (
      <div className="max-w-2xl mx-auto">
        <h1 className="font-display text-[28px] text-[var(--ink)] mb-6">
          Tu <span className="font-display-i text-[var(--ink-3)]">suscripción</span>
        </h1>

        <div className="bg-white rounded-2xl border border-[var(--line)] shadow-sm p-6 mb-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <span className="text-sm text-[var(--ink-3)]">Plan actual</span>
              <p className="text-xl font-bold text-[var(--ink)]">{PLANES[billingStatus.plan]?.name || billingStatus.plan}</p>
            </div>
            <span className="bg-green-100 text-green-700 text-xs font-bold px-3 py-1 rounded-full">Activa</span>
          </div>
          <div className="grid grid-cols-3 gap-4 text-center">
            <div className="bg-[var(--bg-2)] rounded-xl p-3">
              <p className="text-xs text-[var(--ink-3)]">Plazas</p>
              <p className="text-lg font-bold text-[var(--ink)]">{billingStatus.slotsTotal}</p>
            </div>
            <div className="bg-[var(--bg-2)] rounded-xl p-3">
              <p className="text-xs text-[var(--ink-3)]">Usadas</p>
              <p className="text-lg font-bold text-[var(--ink)]">{billingStatus.slotsUsados}</p>
            </div>
            <div className="bg-[var(--bg-2)] rounded-xl p-3">
              <p className="text-xs text-[var(--ink-3)]">Disponibles</p>
              <p className="text-lg font-bold text-green-600">{billingStatus.slotsTotal - billingStatus.slotsUsados}</p>
            </div>
          </div>
          {billingStatus.currentPeriodEnd && (
            <p className="text-xs text-[var(--ink-4)] mt-4">
              Próxima renovación: {formatearFecha(billingStatus.currentPeriodEnd)}
            </p>
          )}
        </div>

        {/* Añadir plazas */}
        <div className="bg-white rounded-2xl border border-[var(--line)] shadow-sm p-6 mb-6">
          <h3 className="font-semibold text-[var(--ink)] mb-4">Ampliar plazas</h3>
          <p className="text-sm text-[var(--ink-2)] mb-4">
            Se cobrará la parte proporcional del tiempo restante del período actual.
          </p>
          <div className="flex gap-3">
            {[1, 5, 10].map(n => (
              <button
                key={n}
                onClick={() => handleAddSlots(n)}
                disabled={loading}
                className="flex-1 py-3 px-4 bg-blue-50 text-blue-700 font-semibold rounded-xl hover:bg-blue-100 transition-colors disabled:opacity-50"
              >
                +{n} plaza{n > 1 ? 's' : ''}
              </button>
            ))}
          </div>
        </div>

        {/* Portal de Stripe */}
        <button
          onClick={handlePortal}
          disabled={loading}
          className="w-full flex items-center justify-center gap-2 py-3 px-6 bg-[var(--bg-2)] text-[var(--ink-2)] font-semibold rounded-xl hover:bg-[var(--line)] transition-colors disabled:opacity-50"
        >
          <FiExternalLink className="h-4 w-4" />
          Gestionar facturación, tarjeta y facturas
        </button>
      </div>
    )
  }

  // ── Sin suscripción: elegir plan ──
  return (
    <div className="max-w-4xl mx-auto">
      <div className="text-center mb-8">
        <h1 className="font-display text-[34px] text-[var(--ink)] mb-2">
          Elige tu <span className="font-display-i text-[var(--ink-3)]">plan</span>
        </h1>
        <p className="text-[14px] text-[var(--ink-3)]">Todos los planes incluyen 4 años de almacenamiento y soporte.</p>
      </div>

      {/* Toggle mensual/anual */}
      <div className="flex flex-col items-center gap-2 mb-8">
        <div className="flex items-center gap-4">
          <span className={`text-sm font-semibold ${interval !== 'yearly' ? 'text-[var(--ink)]' : 'text-[var(--ink-4)]'}`}>Mensual</span>
          <button
            onClick={() => setInterval_(interval === 'yearly' ? 'monthly' : 'yearly')}
            className={`relative flex-shrink-0 w-14 h-7 rounded-full transition-colors ${interval === 'yearly' ? 'bg-blue-600' : 'bg-[var(--line-2)]'}`}
          >
            <span className={`absolute top-1 left-1 w-5 h-5 bg-white rounded-full shadow transition-transform ${interval === 'yearly' ? 'translate-x-7' : 'translate-x-0'}`} />
          </button>
          <span className={`text-sm font-semibold ${interval === 'yearly' ? 'text-[var(--ink)]' : 'text-[var(--ink-4)]'}`}>Anual</span>
        </div>
        {interval === 'yearly' && (
          <span className="inline-flex items-center gap-1 bg-green-100 text-green-700 text-xs font-bold px-3 py-1 rounded-full">
            <FiStar className="h-3 w-3" /> 2 meses gratis
          </span>
        )}
      </div>

      {/* Planes */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
        {Object.entries(PLANES).map(([key, p]) => {
          const isSelected = plan === key
          const price = interval === 'yearly' ? p.priceAnnual : p.priceMonthly
          return (
            <button
              key={key}
              onClick={() => setPlan(key)}
              className={`text-left rounded-2xl p-6 border-2 transition-all ${
                isSelected
                  ? 'border-blue-500 bg-blue-50/50 shadow-md'
                  : 'border-[var(--line)] bg-white hover:border-blue-200'
              }`}
            >
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-lg font-bold text-[var(--ink)]">{p.name}</h3>
                {isSelected && (
                  <div className="w-6 h-6 bg-blue-500 rounded-full flex items-center justify-center">
                    <FiCheckCircle className="h-4 w-4 text-white" />
                  </div>
                )}
              </div>
              <p className="text-sm text-[var(--ink-3)] mb-4">{p.desc}</p>
              <div className="mb-4">
                <span className="text-3xl font-extrabold text-[var(--ink)]">{price.toFixed(2)} €</span>
                <span className="text-sm text-[var(--ink-4)] ml-1">/empleado/mes</span>
              </div>
              <ul className="space-y-2">
                {p.features.map((f, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm text-[var(--ink-2)]">
                    <FiCheckCircle className={`h-4 w-4 mt-0.5 flex-shrink-0 ${isSelected ? 'text-blue-500' : 'text-green-500'}`} />
                    {f}
                  </li>
                ))}
              </ul>
            </button>
          )
        })}
      </div>

      {/* Selector de empleados */}
      <div className="bg-white rounded-2xl border border-[var(--line)] shadow-sm p-6 mb-6">
        <h3 className="font-semibold text-[var(--ink)] mb-1">Número de empleados</h3>
        <p className="text-sm text-[var(--ink-3)] mb-4">Incluye todos los usuarios que van a fichar. Puedes cambiarlo después.</p>
        <div className="flex items-center gap-4 justify-center">
          <button
            onClick={() => setSlots(Math.max(1, slots - 1))}
            className="w-10 h-10 rounded-xl bg-[var(--bg-2)] flex items-center justify-center hover:bg-[var(--line)] transition-colors"
          >
            <FiMinus className="h-4 w-4 text-[var(--ink-2)]" />
          </button>
          <input
            type="number"
            min="1"
            max="999"
            value={slots}
            onChange={e => setSlots(Math.max(1, parseInt(e.target.value) || 1))}
            className="w-20 text-center text-2xl font-extrabold text-[var(--ink)] border-b-2 border-[var(--line)] focus:border-blue-500 outline-none py-1"
          />
          <button
            onClick={() => setSlots(slots + 1)}
            className="w-10 h-10 rounded-xl bg-[var(--bg-2)] flex items-center justify-center hover:bg-[var(--line)] transition-colors"
          >
            <FiPlus className="h-4 w-4 text-[var(--ink-2)]" />
          </button>
        </div>
      </div>

      {/* Resumen y CTA */}
      <div className="bg-gradient-to-r from-blue-600 to-blue-700 rounded-2xl p-6 text-white">
        <div className="flex items-center justify-between mb-4">
          <div>
            <p className="text-blue-100 text-sm">Plan {selectedPlan.name} · {slots} empleado{slots > 1 ? 's' : ''} · {interval === 'yearly' ? 'Anual' : 'Mensual'}</p>
            <p className="text-3xl font-extrabold mt-1">
              {total.toFixed(2)} €<span className="text-lg text-blue-200 font-normal">/{interval === 'yearly' ? 'mes' : 'mes'}</span>
            </p>
            {interval === 'yearly' && (
              <p className="text-xs text-blue-200 mt-1">
                Facturación anual: {(total * 12).toFixed(2)} € / Equivale a {(pricePerSlot).toFixed(2)} €/emp/mes
              </p>
            )}
          </div>
          <FiShield className="h-10 w-10 text-blue-200" />
        </div>
        <button
          onClick={handleCheckout}
          disabled={loading}
          className="w-full bg-white text-blue-700 font-bold py-4 px-8 rounded-xl text-base shadow-lg hover:bg-blue-50 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
        >
          {loading ? 'Procesando...' : (
            <>
              <FiCreditCard className="h-5 w-5" />
              Activar suscripción
            </>
          )}
        </button>
        <div className="flex items-center justify-center gap-4 mt-4 text-xs text-blue-200">
          <span>Pago seguro con Stripe</span>
          <span>·</span>
          <span>Cancela cuando quieras</span>
          <span>·</span>
          <span>Sin permanencia</span>
        </div>
      </div>
    </div>
  )
}
